import type { Person, PersonId, Role, Stats } from './types';

/**
 * Role affinity multipliers. A person in the right seat multiplies their stats
 * up; a person in the wrong seat gets dragged down *and* adds chaos (see
 * `scoring.ts`). This is the main lever that makes "where do you draft them?"
 * an actual decision.
 */
export const AFFINITY = {
  perfect: 1.12,
  great: 1.05,
  good: 0.97,
  ok: 0.88,
  poor: 0.68,
  awful: 0.5,
} as const;

/** Applied when a person has no declared opinion about a role. */
export const DEFAULT_AFFINITY = 0.78;
/** The wildcard seat is forgiving by design. */
export const WILDCARD_AFFINITY = 0.9;

const A = AFFINITY;

/** [product, engineering, growth, execution, fundraising, chaos] */
type StatTuple = [number, number, number, number, number, number];

const toStats = ([product, engineering, growth, execution, fundraising, chaos]: StatTuple): Stats => ({
  product,
  engineering,
  growth,
  execution,
  fundraising,
  chaos,
});

const initialsOf = (name: string) =>
  name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const p = (
  id: PersonId,
  name: string,
  tagline: string,
  tags: string[],
  stats: StatTuple,
  affinity: Partial<Record<Role, number>>,
): Person => ({ id, name, initials: initialsOf(name), tagline, tags, stats: toStats(stats), affinity });

const ROSTER: Person[] = [
  // ── PayPal Mafia ────────────────────────────────────────────────────────
  p('peter-thiel', 'Peter Thiel', 'Contrarian in chief', ['Fundraising monster', 'Zero to one'],
    [78, 62, 70, 72, 96, 62], { CEO: A.good, CPO: A.ok, GROWTH: A.ok, WILDCARD: 1.06, CTO: A.poor }),
  p('elon-musk', 'Elon Musk', 'Chief posting officer', ['High chaos', 'Distribution cheat code'],
    [92, 88, 95, 80, 94, 97], { CEO: A.great, CTO: A.good, GROWTH: A.perfect, COO: A.awful, WILDCARD: 1.02 }),
  p('reid-hoffman', 'Reid Hoffman', 'Network effects whisperer', ['Elite connector', 'Low drama'],
    [74, 60, 86, 72, 90, 25], { CEO: A.ok, GROWTH: A.great, WILDCARD: 0.98, CTO: A.poor }),
  p('max-levchin', 'Max Levchin', 'Fraud detection savant', ['Deep technical', 'Ships hard things'],
    [72, 94, 58, 74, 66, 40], { CTO: A.perfect, CPO: A.ok, CEO: A.ok, GROWTH: A.poor }),
  p('david-sacks', 'David Sacks', 'Operator with a podcast', ['Elite operator', 'Opinionated'],
    [76, 55, 74, 86, 78, 66], { COO: A.perfect, CEO: A.good, CPO: A.ok, CTO: A.awful }),
  p('keith-rabois', 'Keith Rabois', 'Aggressive talent magnet', ['Elite operator', 'High chaos'],
    [66, 52, 78, 90, 76, 72], { COO: A.great, GROWTH: A.great, CEO: A.ok, CTO: A.awful }),
  p('roelof-botha', 'Roelof Botha', 'Quiet capital', ['Fundraising monster', 'Low drama'],
    [58, 50, 62, 80, 92, 18], { CEO: A.ok, COO: A.good, WILDCARD: 1.02, CTO: A.awful, CPO: A.poor }),
  p('jawed-karim', 'Jawed Karim', 'First video ever', ['Deep technical', 'Underrated'],
    [70, 82, 66, 50, 40, 30], { CTO: A.great, CPO: A.good, WILDCARD: A.good, CEO: A.poor }),

  // ── Apple 2007 ──────────────────────────────────────────────────────────
  p('steve-jobs', 'Steve Jobs', 'Reality distortion field', ['Product visionary', 'High chaos'],
    [99, 62, 88, 84, 90, 78], { CEO: A.perfect, CPO: A.great, CTO: A.poor, COO: A.poor, GROWTH: A.good }),
  p('jony-ive', 'Jony Ive', 'Aluminium poet', ['Product visionary', 'Design obsessive'],
    [96, 58, 54, 66, 50, 34], { CPO: A.perfect, CEO: A.poor, CTO: A.poor, COO: A.awful, GROWTH: A.poor }),
  p('tim-cook', 'Tim Cook', 'Supply chain assassin', ['Elite operator', 'Low drama'],
    [52, 60, 66, 99, 80, 10], { COO: A.perfect, CEO: A.great, CTO: A.poor, CPO: A.awful }),
  p('scott-forstall', 'Scott Forstall', 'Skeuomorph tyrant', ['Deep technical', 'High chaos'],
    [84, 88, 52, 74, 44, 68], { CTO: A.great, CPO: A.great, COO: A.poor, CEO: A.poor }),
  p('phil-schiller', 'Phil Schiller', 'Keynote hype machine', ['Distribution', 'Company man'],
    [70, 44, 80, 64, 48, 26], { GROWTH: A.great, CPO: A.ok, CEO: A.poor, CTO: A.awful }),
  p('tony-fadell', 'Tony Fadell', 'Hardware perfectionist', ['Product visionary', 'Deep technical'],
    [88, 84, 58, 76, 62, 52], { CPO: A.perfect, CTO: A.great, CEO: A.ok, GROWTH: A.poor }),
  p('bertrand-serlet', 'Bertrand Serlet', 'Operating system architect', ['Deep technical', 'Low drama'],
    [58, 92, 36, 68, 32, 24], { CTO: A.perfect, CPO: A.ok, CEO: A.awful, GROWTH: A.awful }),

  // ── Facebook 2012 ───────────────────────────────────────────────────────
  p('mark-zuckerberg', 'Mark Zuckerberg', 'Domination speedrun', ['Product visionary', 'Relentless'],
    [88, 90, 92, 88, 88, 58], { CEO: A.perfect, CTO: A.great, CPO: A.great, GROWTH: A.good, COO: A.poor }),
  p('sheryl-sandberg', 'Sheryl Sandberg', 'Monetisation engine', ['Elite operator', 'Fundraising monster'],
    [58, 48, 94, 92, 86, 30], { COO: A.perfect, GROWTH: A.great, CEO: A.ok, CTO: A.awful, CPO: A.poor }),
  p('andrew-bosworth', 'Andrew Bosworth', 'Ships first, apologises later', ['Deep technical', 'High chaos'],
    [66, 86, 72, 80, 40, 64], { CTO: A.great, COO: A.good, CPO: A.ok, CEO: A.poor }),
  p('chris-cox', 'Chris Cox', 'Product soul of the company', ['Product visionary', 'Low drama'],
    [90, 70, 70, 78, 52, 20], { CPO: A.perfect, CEO: A.good, COO: A.ok, CTO: A.poor }),
  p('mike-schroepfer', 'Mike Schroepfer', 'Scales the unscalable', ['Deep technical', 'Elite operator'],
    [54, 94, 50, 82, 44, 16], { CTO: A.perfect, COO: A.good, CEO: A.poor, GROWTH: A.poor }),
  p('naomi-gleit', 'Naomi Gleit', 'Wrote the growth playbook', ['Growth machine', 'Elite operator'],
    [70, 50, 88, 90, 42, 14], { GROWTH: A.perfect, COO: A.great, CPO: A.ok, CTO: A.awful }),
  p('javier-olivan', 'Javier Olivan', 'Growth, industrialised', ['Growth machine', 'Low drama'],
    [56, 60, 92, 86, 40, 20], { GROWTH: A.perfect, COO: A.great, CEO: A.poor, CTO: A.poor }),

  // ── OpenAI 2023 ─────────────────────────────────────────────────────────
  p('sam-altman', 'Sam Altman', 'Raises money from physics', ['Fundraising monster', 'Survivor'],
    [82, 66, 90, 84, 99, 74], { CEO: A.perfect, GROWTH: A.great, COO: A.ok, CTO: A.poor, WILDCARD: 1.02 }),
  p('ilya-sutskever', 'Ilya Sutskever', 'Feel the AGI', ['Deep technical', 'Unpredictable'],
    [72, 99, 44, 58, 60, 56], { CTO: A.perfect, WILDCARD: 0.98, CEO: A.poor, COO: A.awful, GROWTH: A.awful }),
  p('greg-brockman', 'Greg Brockman', 'Never stops typing', ['Deep technical', 'Relentless'],
    [74, 96, 62, 88, 58, 38], { CTO: A.perfect, COO: A.great, CEO: A.ok, CPO: A.ok }),
  p('mira-murati', 'Mira Murati', 'Calm during the coup', ['Elite operator', 'Product visionary'],
    [84, 78, 68, 86, 72, 44], { CPO: A.great, COO: A.great, CEO: A.good, CTO: A.ok }),
  p('andrej-karpathy', 'Andrej Karpathy', 'Explains it better than you', ['Deep technical', 'Distribution'],
    [80, 96, 74, 60, 48, 26], { CTO: A.perfect, CPO: A.good, GROWTH: A.ok, COO: A.poor }),
  p('jakub-pachocki', 'Jakub Pachocki', 'Scaling laws enjoyer', ['Deep technical', 'Heads down'],
    [64, 97, 40, 66, 42, 22], { CTO: A.perfect, CEO: A.awful, GROWTH: A.awful, COO: A.poor }),
  p('brad-lightcap', 'Brad Lightcap', 'Closes every deal', ['Fundraising monster', 'Elite operator'],
    [50, 44, 78, 88, 90, 22], { COO: A.perfect, GROWTH: A.great, CEO: A.ok, CTO: A.awful }),

  // ── Google 2004 ─────────────────────────────────────────────────────────
  p('larry-page', 'Larry Page', 'Absentee visionary', ['Product visionary', 'Deep technical'],
    [86, 90, 72, 58, 80, 62], { CEO: A.good, CTO: A.great, CPO: A.great, COO: A.awful }),
  p('sergey-brin', 'Sergey Brin', 'Moonshot goblin', ['Deep technical', 'High chaos'],
    [78, 94, 60, 54, 74, 58], { CTO: A.perfect, WILDCARD: 1.02, CPO: A.good, COO: A.awful, CEO: A.poor }),
  p('eric-schmidt', 'Eric Schmidt', 'Adult supervision', ['Elite operator', 'Low drama'],
    [56, 68, 80, 94, 88, 18], { CEO: A.great, COO: A.perfect, CTO: A.ok, CPO: A.poor }),
  p('marissa-mayer', 'Marissa Mayer', 'Forty shades of blue', ['Product visionary', 'Divisive'],
    [80, 74, 64, 70, 58, 54], { CPO: A.great, CEO: A.ok, CTO: A.ok, COO: A.poor }),
  p('jeff-dean', 'Jeff Dean', 'Compiles by staring at it', ['Deep technical', 'Legend'],
    [60, 100, 44, 76, 38, 10], { CTO: A.perfect, COO: A.ok, CEO: A.awful, GROWTH: A.awful }),
  p('susan-wojcicki', 'Susan Wojcicki', 'Owns the garage and the ads', ['Growth machine', 'Elite operator'],
    [70, 56, 88, 84, 54, 22], { GROWTH: A.perfect, CEO: A.good, COO: A.great, CTO: A.awful }),
  p('salar-kamangar', 'Salar Kamangar', 'Turned video into money', ['Growth machine', 'Product visionary'],
    [76, 66, 84, 78, 50, 20], { GROWTH: A.great, CPO: A.great, CEO: A.ok, CTO: A.poor }),

  // ── Crypto 2021 ─────────────────────────────────────────────────────────
  p('sam-bankman-fried', 'Sam Bankman-Fried', 'Effective at something', ['High chaos', 'Fundraising monster'],
    [58, 72, 84, 50, 96, 99], { CEO: A.ok, GROWTH: A.great, WILDCARD: 1.06, COO: A.awful, CPO: A.poor }),
  p('changpeng-zhao', 'Changpeng Zhao', 'Offshore and everywhere', ['Growth machine', 'High chaos'],
    [64, 80, 94, 84, 88, 84], { CEO: A.great, GROWTH: A.perfect, COO: A.good, CPO: A.poor }),
  p('vitalik-buterin', 'Vitalik Buterin', 'Wrote the whitepaper at 19', ['Deep technical', 'Cult following'],
    [88, 98, 66, 54, 70, 40], { CTO: A.perfect, CPO: A.great, WILDCARD: 1.02, COO: A.awful, CEO: A.poor }),
  p('brian-armstrong', 'Brian Armstrong', 'Regulated on purpose', ['Elite operator', 'Low drama'],
    [66, 74, 78, 84, 86, 36], { CEO: A.great, COO: A.great, CTO: A.ok, CPO: A.ok }),
  p('do-kwon', 'Do Kwon', 'Algorithmically stable', ['High chaos', 'Terminally online'],
    [54, 66, 88, 44, 84, 99], { GROWTH: A.great, CEO: A.poor, COO: A.awful, WILDCARD: 0.98 }),
  p('michael-saylor', 'Michael Saylor', 'Buys the top forever', ['Fundraising monster', 'High conviction'],
    [40, 58, 76, 60, 94, 80], { CEO: A.ok, GROWTH: A.good, WILDCARD: 1.02, CPO: A.awful, CTO: A.poor }),
  p('su-zhu', 'Su Zhu', 'Supercycle believer', ['High chaos', 'Leverage enjoyer'],
    [36, 44, 70, 38, 88, 96], { GROWTH: A.ok, WILDCARD: A.good, CEO: A.awful, COO: A.awful, CTO: A.awful }),

  // ── SaaS Founders 2018 ──────────────────────────────────────────────────
  p('marc-benioff', 'Marc Benioff', 'No software, huge yacht', ['Growth machine', 'Fundraising monster'],
    [72, 58, 92, 84, 94, 54], { CEO: A.perfect, GROWTH: A.great, COO: A.ok, CTO: A.awful }),
  p('stewart-butterfield', 'Stewart Butterfield', 'Failed game became Slack', ['Product visionary', 'Charming'],
    [92, 66, 74, 62, 74, 38], { CPO: A.perfect, CEO: A.great, GROWTH: A.good, COO: A.poor }),
  p('aaron-levie', 'Aaron Levie', 'Enterprise, but funny', ['Distribution', 'Elite operator'],
    [76, 64, 82, 74, 80, 34], { CEO: A.great, GROWTH: A.great, CPO: A.good, CTO: A.poor }),
  p('tobi-lutke', 'Tobi Lütke', 'Merchant-obsessed engineer', ['Deep technical', 'Product visionary'],
    [88, 92, 76, 80, 72, 30], { CEO: A.great, CTO: A.perfect, CPO: A.great, COO: A.ok }),
  p('melanie-perkins', 'Melanie Perkins', 'Design for absolutely everyone', ['Product visionary', 'Growth machine'],
    [94, 60, 88, 82, 78, 12], { CEO: A.great, CPO: A.perfect, GROWTH: A.great, CTO: A.poor }),
  p('frank-slootman', 'Frank Slootman', 'Amp it up', ['Elite operator', 'Ruthless'],
    [44, 52, 84, 99, 80, 44], { CEO: A.great, COO: A.perfect, GROWTH: A.good, CPO: A.awful, CTO: A.awful }),
  p('eric-yuan', 'Eric Yuan', 'Happiness delivered over video', ['Elite operator', 'Deep technical'],
    [82, 86, 80, 90, 64, 8], { CEO: A.great, CTO: A.great, COO: A.great, CPO: A.good }),

  // ── Tech Villains ───────────────────────────────────────────────────────
  p('adam-neumann', 'Adam Neumann', 'Elevates world consciousness', ['High chaos', 'Fundraising monster'],
    [64, 30, 92, 38, 99, 98], { GROWTH: A.perfect, CEO: A.ok, WILDCARD: 1.02, COO: A.awful, CTO: A.awful }),
  p('elizabeth-holmes', 'Elizabeth Holmes', 'One drop of blood', ['High chaos', 'Storyteller'],
    [50, 18, 80, 30, 96, 99], { GROWTH: A.great, CEO: A.poor, CTO: A.awful, COO: A.awful, WILDCARD: 1.02 }),
  p('travis-kalanick', 'Travis Kalanick', 'Growth at any cost', ['Growth machine', 'High chaos'],
    [70, 64, 99, 86, 92, 94], { GROWTH: A.perfect, CEO: A.good, COO: A.great, CPO: A.poor }),
  p('martin-shkreli', 'Martin Shkreli', 'Priced it wrong on purpose', ['High chaos', 'Villain arc'],
    [30, 52, 64, 54, 78, 99], { WILDCARD: 1.06, GROWTH: A.ok, CEO: A.awful, COO: A.awful, CPO: A.awful }),
  p('trevor-milton', 'Trevor Milton', 'The truck rolled downhill', ['High chaos', 'Storyteller'],
    [40, 22, 74, 32, 88, 97], { GROWTH: A.good, WILDCARD: A.good, CTO: A.awful, COO: A.awful, CEO: A.awful }),

  // ── Twitter ─────────────────────────────────────────────────────────────
  p('jack-dorsey', 'Jack Dorsey', 'Cryptic ascetic', ['Product visionary', 'Absent'],
    [78, 64, 70, 42, 76, 72], { CPO: A.great, WILDCARD: 1.02, CEO: A.ok, COO: A.awful }),
  p('dick-costolo', 'Dick Costolo', 'Comedian turned CEO', ['Elite operator', 'Low drama'],
    [56, 52, 78, 76, 72, 26], { COO: A.great, CEO: A.good, GROWTH: A.good, CTO: A.awful }),
  p('ev-williams', 'Ev Williams', 'Serial platform founder', ['Product visionary', 'Restless'],
    [82, 60, 68, 50, 70, 34], { CPO: A.great, CEO: A.ok, GROWTH: A.ok, COO: A.poor }),
  p('biz-stone', 'Biz Stone', 'Vibes cofounder', ['Brand', 'Low drama'],
    [64, 48, 72, 52, 60, 30], { GROWTH: A.good, WILDCARD: A.good, CPO: A.ok, CTO: A.awful }),
  p('parag-agrawal', 'Parag Agrawal', 'Inherited the fire', ['Deep technical', 'Unlucky'],
    [50, 80, 44, 62, 40, 28], { CTO: A.great, CEO: A.poor, GROWTH: A.awful, CPO: A.poor }),
  p('linda-yaccarino', 'Linda Yaccarino', 'Advertisers, please come back', ['Distribution', 'Enterprise sales'],
    [40, 30, 84, 66, 70, 52], { GROWTH: A.great, COO: A.good, CEO: A.poor, CTO: A.awful }),

  // ── Silicon 2024 ────────────────────────────────────────────────────────
  p('jensen-huang', 'Jensen Huang', 'Leather jacket, infinite moat', ['Deep technical', 'Elite operator'],
    [86, 96, 88, 94, 90, 44], { CEO: A.perfect, CTO: A.perfect, CPO: A.great, COO: A.good }),
  p('lisa-su', 'Lisa Su', 'The impossible turnaround', ['Deep technical', 'Elite operator'],
    [72, 94, 74, 96, 76, 12], { CEO: A.great, CTO: A.perfect, COO: A.great, CPO: A.ok }),
  p('morris-chang', 'Morris Chang', 'Invented the foundry', ['Elite operator', 'Legend'],
    [60, 92, 66, 99, 72, 10], { COO: A.perfect, CEO: A.great, CTO: A.great, GROWTH: A.poor }),
  p('pat-gelsinger', 'Pat Gelsinger', 'Believer in comebacks', ['Deep technical', 'Optimist'],
    [56, 86, 54, 70, 64, 38], { CTO: A.great, CEO: A.ok, COO: A.good, GROWTH: A.poor }),
  p('jim-keller', 'Jim Keller', 'Legendary chip, then leaves', ['Deep technical', 'Flight risk'],
    [58, 99, 40, 72, 44, 50], { CTO: A.perfect, WILDCARD: 0.98, CEO: A.poor, GROWTH: A.awful }),
  p('hock-tan', 'Hock Tan', 'Acquire, then raise the price', ['Ruthless', 'Elite operator'],
    [34, 66, 70, 96, 86, 58], { COO: A.perfect, CEO: A.great, GROWTH: A.ok, CPO: A.awful }),

  // ── Amazon 2015 ─────────────────────────────────────────────────────────
  p('jeff-bezos', 'Jeff Bezos', 'Day 1, forever', ['Elite operator', 'Relentless'],
    [84, 82, 94, 96, 92, 52], { CEO: A.perfect, COO: A.great, GROWTH: A.great, CPO: A.good }),
  p('andy-jassy', 'Andy Jassy', 'Built the cloud quietly', ['Elite operator', 'Low drama'],
    [62, 80, 74, 90, 66, 16], { COO: A.perfect, CEO: A.good, CTO: A.good, CPO: A.poor }),
  p('werner-vogels', 'Werner Vogels', 'Everything fails, all the time', ['Deep technical', 'Distribution'],
    [60, 96, 52, 76, 40, 20], { CTO: A.perfect, CPO: A.ok, CEO: A.poor, GROWTH: A.poor }),
  p('jeff-wilke', 'Jeff Wilke', 'Logistics as a superpower', ['Elite operator', 'Systems brain'],
    [48, 66, 60, 97, 52, 18], { COO: A.perfect, CTO: A.ok, CEO: A.poor, CPO: A.awful }),
  p('dave-limp', 'Dave Limp', 'Ships devices relentlessly', ['Product visionary', 'Hardware'],
    [70, 68, 58, 80, 46, 26], { CPO: A.great, COO: A.good, CTO: A.ok, GROWTH: A.poor }),

  // ── Microsoft 1998 ──────────────────────────────────────────────────────
  p('bill-gates', 'Bill Gates', 'Reads your API and wins', ['Deep technical', 'Ruthless'],
    [78, 94, 84, 90, 84, 50], { CEO: A.perfect, CTO: A.great, CPO: A.great, COO: A.good }),
  p('steve-ballmer', 'Steve Ballmer', 'DEVELOPERS DEVELOPERS', ['High chaos', 'Growth machine'],
    [40, 54, 94, 84, 80, 84], { GROWTH: A.perfect, COO: A.good, CEO: A.ok, CPO: A.awful, CTO: A.awful }),
  p('satya-nadella', 'Satya Nadella', 'Quiet trillion-dollar pivot', ['Elite operator', 'Low drama'],
    [80, 84, 80, 94, 82, 10], { CEO: A.perfect, COO: A.great, CTO: A.great, CPO: A.good }),
  p('paul-allen', 'Paul Allen', 'The other founder', ['Deep technical', 'Underrated'],
    [70, 88, 54, 60, 76, 34], { CTO: A.great, WILDCARD: 0.98, CPO: A.ok, COO: A.poor }),
  p('kevin-scott', 'Kevin Scott', 'CTO of the AI pivot', ['Deep technical', 'Diplomatic'],
    [66, 88, 50, 74, 50, 18], { CTO: A.perfect, COO: A.ok, CEO: A.poor, GROWTH: A.poor }),
  p('anders-hejlsberg', 'Anders Hejlsberg', 'Designed the language you use', ['Deep technical', 'Legend'],
    [64, 99, 30, 70, 28, 8], { CTO: A.perfect, CPO: A.ok, CEO: A.awful, GROWTH: A.awful, COO: A.poor }),

  // ── AI Wave 2025 ────────────────────────────────────────────────────────
  p('dario-amodei', 'Dario Amodei', 'Scaling, with a safety case', ['Deep technical', 'Fundraising monster'],
    [80, 96, 66, 82, 92, 30], { CEO: A.great, CTO: A.perfect, CPO: A.good, GROWTH: A.poor }),
  p('demis-hassabis', 'Demis Hassabis', 'Chess prodigy, Nobel laureate', ['Deep technical', 'Legend'],
    [82, 98, 60, 84, 80, 22], { CTO: A.perfect, CEO: A.great, CPO: A.good, GROWTH: A.poor }),
  p('mustafa-suleyman', 'Mustafa Suleyman', 'Always at the frontier', ['Fundraising monster', 'Divisive'],
    [76, 74, 80, 70, 86, 52], { CEO: A.good, CPO: A.good, GROWTH: A.great, CTO: A.ok }),
  p('aravind-srinivas', 'Aravind Srinivas', 'Answers, not links', ['Growth machine', 'Product visionary'],
    [82, 80, 90, 72, 84, 44], { CEO: A.great, CPO: A.great, GROWTH: A.perfect, COO: A.poor }),
  p('alexandr-wang', 'Alexandr Wang', 'From data labels to defence', ['Fundraising monster', 'Relentless'],
    [66, 78, 88, 86, 92, 48], { CEO: A.great, GROWTH: A.great, COO: A.good, CPO: A.ok }),
  p('emad-mostaque', 'Emad Mostaque', 'Open sourced it, then vanished', ['High chaos', 'Storyteller'],
    [60, 72, 84, 40, 86, 92], { GROWTH: A.good, WILDCARD: 1.02, CEO: A.poor, COO: A.awful }),

  // ── Dot-com 1999 ────────────────────────────────────────────────────────
  p('marc-andreessen', 'Marc Andreessen', 'Software eats everything', ['Deep technical', 'Fundraising monster'],
    [82, 88, 74, 64, 94, 66], { CTO: A.great, CEO: A.good, WILDCARD: 1.06, COO: A.poor }),
  p('jerry-yang', 'Jerry Yang', 'Directory of the internet', ['Product visionary', 'Nice guy'],
    [64, 66, 76, 58, 72, 34], { CEO: A.ok, CPO: A.good, GROWTH: A.good, COO: A.poor }),
  p('mark-cuban', 'Mark Cuban', 'Sold at the exact top', ['Distribution', 'Loud'],
    [58, 54, 86, 70, 88, 70], { GROWTH: A.great, CEO: A.ok, WILDCARD: 1.02, CTO: A.awful }),
  p('steve-case', 'Steve Case', 'Mailed everyone a CD-ROM', ['Distribution', 'Growth machine'],
    [60, 50, 88, 66, 84, 44], { GROWTH: A.perfect, CEO: A.ok, COO: A.ok, CTO: A.awful }),
  p('masayoshi-son', 'Masayoshi Son', '300-year plan, instant yes', ['Fundraising monster', 'High chaos'],
    [54, 50, 82, 58, 99, 90], { CEO: A.ok, GROWTH: A.great, WILDCARD: 1.02, COO: A.awful, CTO: A.awful }),

  // ── Capital 2019 ────────────────────────────────────────────────────────
  p('ben-horowitz', 'Ben Horowitz', 'Wrote the book on hard things', ['Elite operator', 'Fundraising monster'],
    [58, 60, 70, 86, 88, 32], { COO: A.great, CEO: A.good, WILDCARD: 0.98, CTO: A.poor }),
  p('paul-graham', 'Paul Graham', 'Make something people want', ['Product visionary', 'Deep technical'],
    [86, 84, 66, 60, 72, 20], { CPO: A.great, CTO: A.good, WILDCARD: 1.02, COO: A.poor }),
  p('naval-ravikant', 'Naval Ravikant', 'Tweets only in aphorisms', ['Brand', 'Fundraising monster'],
    [72, 58, 74, 52, 84, 40], { WILDCARD: 1.06, GROWTH: A.good, CEO: A.ok, COO: A.awful }),
  p('chamath-palihapitiya', 'Chamath Palihapitiya', 'SPAC absolutely everything', ['High chaos', 'Growth machine'],
    [56, 52, 90, 60, 96, 88], { GROWTH: A.perfect, CEO: A.poor, WILDCARD: 1.06, COO: A.awful }),
  p('bill-gurley', 'Bill Gurley', 'Warns you about the bubble', ['Low drama', 'Fundraising monster'],
    [50, 48, 66, 80, 86, 30], { COO: A.good, WILDCARD: 0.98, CEO: A.ok, CTO: A.awful, CPO: A.poor }),

  // ── Apple / Google / Microsoft additions ────────────────────────────────
  p('eddy-cue', 'Eddy Cue', 'Deals with the labels', ['Distribution', 'Enterprise sales'],
    [58, 44, 82, 74, 64, 30], { GROWTH: A.great, COO: A.good, CEO: A.poor, CTO: A.awful }),
  p('sundar-pichai', 'Sundar Pichai', 'Won the browser war politely', ['Elite operator', 'Low drama'],
    [74, 72, 78, 92, 76, 10], { COO: A.perfect, CEO: A.great, CPO: A.good, CTO: A.ok }),
  p('urs-holzle', 'Urs Hölzle', 'Built the datacentre', ['Deep technical', 'Systems brain'],
    [48, 97, 40, 86, 36, 12], { CTO: A.perfect, COO: A.great, CEO: A.awful, GROWTH: A.awful }),
  p('charles-simonyi', 'Charles Simonyi', 'Invented the word processor', ['Deep technical', 'Legend'],
    [72, 95, 38, 68, 40, 26], { CTO: A.perfect, CPO: A.good, CEO: A.awful, GROWTH: A.awful }),
  p('ray-ozzie', 'Ray Ozzie', 'Groupware before the cloud', ['Deep technical', 'Product visionary'],
    [78, 88, 44, 66, 46, 24], { CTO: A.perfect, CPO: A.great, CEO: A.poor, GROWTH: A.poor }),
  p('dustin-moskovitz', 'Dustin Moskovitz', 'Left, then built the to-do list', ['Deep technical', 'Low drama'],
    [72, 86, 58, 84, 48, 14], { CTO: A.great, COO: A.great, CPO: A.good, GROWTH: A.poor }),
  p('wojciech-zaremba', 'Wojciech Zaremba', 'Robots first, then code', ['Deep technical', 'Heads down'],
    [66, 95, 42, 64, 44, 20], { CTO: A.perfect, CPO: A.ok, CEO: A.awful, GROWTH: A.awful }),

  // ── Tesla, Model S era ──────────────────────────────────────────────────
  p('jb-straubel', 'JB Straubel', 'The battery is the company', ['Deep technical', 'Underrated'],
    [70, 96, 44, 84, 52, 18], { CTO: A.perfect, COO: A.great, CPO: A.ok, GROWTH: A.awful }),
  p('franz-von-holzhausen', 'Franz von Holzhausen', 'Drew it in one weekend', ['Design obsessive', 'Product visionary'],
    [90, 52, 60, 70, 44, 36], { CPO: A.perfect, WILDCARD: 1.02, CEO: A.poor, CTO: A.awful }),
  p('drew-baglino', 'Drew Baglino', 'Powertrain, industrialised', ['Deep technical', 'Elite operator'],
    [58, 90, 42, 86, 40, 16], { CTO: A.great, COO: A.great, CEO: A.awful, GROWTH: A.awful }),

  // ── Uber, hypergrowth ───────────────────────────────────────────────────
  p('garrett-camp', 'Garrett Camp', 'Had the idea in the snow', ['Product visionary', 'Absent'],
    [82, 62, 66, 44, 72, 44], { CPO: A.great, WILDCARD: 1.02, CEO: A.poor, COO: A.awful }),
  p('ryan-graves', 'Ryan Graves', 'Replied to the tweet', ['Elite operator', 'Underrated'],
    [50, 48, 76, 88, 52, 30], { COO: A.perfect, GROWTH: A.great, CEO: A.ok, CTO: A.awful }),
  p('emil-michael', 'Emil Michael', 'Opposition research guy', ['High chaos', 'Fundraising monster'],
    [42, 44, 80, 72, 92, 88], { GROWTH: A.great, COO: A.good, CEO: A.poor, CPO: A.awful }),
  p('thuan-pham', 'Thuan Pham', 'Kept it up at 10x', ['Deep technical', 'Elite operator'],
    [48, 92, 46, 90, 36, 14], { CTO: A.perfect, COO: A.great, CEO: A.awful, GROWTH: A.awful }),

  // ── Twitter, founding era ───────────────────────────────────────────────
  p('noah-glass', 'Noah Glass', 'Named it, then erased', ['Product visionary', 'Unlucky'],
    [80, 58, 64, 40, 44, 58], { CPO: A.great, WILDCARD: 1.02, CEO: A.poor, COO: A.awful }),
  p('jason-goldman', 'Jason Goldman', 'The adult in the product room', ['Elite operator', 'Low drama'],
    [72, 54, 60, 80, 42, 18], { CPO: A.great, COO: A.great, CEO: A.poor, CTO: A.poor }),

  // ── Airbnb, breakout ────────────────────────────────────────────────────
  p('brian-chesky', 'Brian Chesky', 'Design school runs the company', ['Product visionary', 'Founder mode'],
    [94, 58, 82, 84, 90, 46], { CEO: A.perfect, CPO: A.perfect, GROWTH: A.good, CTO: A.awful }),
  p('joe-gebbia', 'Joe Gebbia', 'Sold cereal to survive', ['Design obsessive', 'Product visionary'],
    [88, 52, 72, 62, 66, 34], { CPO: A.perfect, WILDCARD: 1.02, COO: A.poor, CTO: A.awful }),
  p('nathan-blecharczyk', 'Nathan Blecharczyk', 'The only one who could code', ['Deep technical', 'Growth machine'],
    [64, 90, 84, 78, 54, 18], { CTO: A.perfect, GROWTH: A.great, COO: A.good, CEO: A.poor }),
  p('belinda-johnson', 'Belinda Johnson', 'Talks the regulators down', ['Elite operator', 'Low drama'],
    [40, 36, 62, 94, 60, 12], { COO: A.perfect, CEO: A.ok, CPO: A.awful, CTO: A.awful }),
  p('jonathan-mildenhall', 'Jonathan Mildenhall', 'Belong anywhere', ['Brand', 'Distribution'],
    [62, 28, 88, 60, 52, 36], { GROWTH: A.perfect, CPO: A.ok, COO: A.poor, CTO: A.awful }),

  // ── DeepMind ────────────────────────────────────────────────────────────
  p('shane-legg', 'Shane Legg', 'Named the AGI', ['Deep technical', 'Heads down'],
    [58, 94, 38, 62, 54, 22], { CTO: A.perfect, WILDCARD: 0.98, CEO: A.awful, GROWTH: A.awful }),
  p('david-silver', 'David Silver', 'Taught it to play Go', ['Deep technical', 'Legend'],
    [64, 97, 44, 70, 40, 16], { CTO: A.perfect, CPO: A.ok, CEO: A.awful, COO: A.poor }),
  p('oriol-vinyals', 'Oriol Vinyals', 'Sequence to sequence', ['Deep technical', 'Relentless'],
    [62, 96, 46, 72, 42, 18], { CTO: A.perfect, CPO: A.good, CEO: A.awful, GROWTH: A.poor }),

  // ── Wiz ─────────────────────────────────────────────────────────────────
  p('assaf-rappaport', 'Assaf Rappaport', 'Sold it twice, said no once', ['Elite operator', 'Fundraising monster'],
    [82, 84, 88, 90, 94, 28], { CEO: A.perfect, GROWTH: A.great, COO: A.great, CTO: A.good }),
  p('ami-luttwak', 'Ami Luttwak', 'Finds the hole in your cloud', ['Deep technical', 'Product visionary'],
    [84, 96, 58, 76, 50, 20], { CTO: A.perfect, CPO: A.great, CEO: A.poor, GROWTH: A.poor }),
  p('yinon-costica', 'Yinon Costica', 'Turns research into roadmap', ['Product visionary', 'Elite operator'],
    [86, 74, 72, 82, 48, 16], { CPO: A.perfect, COO: A.great, CEO: A.ok, CTO: A.good }),
  p('roy-reznik', 'Roy Reznik', 'Ships the whole platform', ['Deep technical', 'Relentless'],
    [66, 94, 50, 86, 40, 14], { CTO: A.perfect, COO: A.great, CEO: A.poor, GROWTH: A.awful }),

  // ── monday.com ──────────────────────────────────────────────────────────
  p('roy-mann', 'Roy Mann', 'A board for absolutely everything', ['Product visionary', 'Growth machine'],
    [88, 66, 84, 78, 80, 24], { CPO: A.perfect, CEO: A.great, GROWTH: A.great, CTO: A.poor }),
  p('eran-zinman', 'Eran Zinman', 'The other half of the desk', ['Deep technical', 'Elite operator'],
    [76, 88, 72, 86, 64, 14], { CTO: A.perfect, CEO: A.great, COO: A.great, CPO: A.good }),
  p('daniel-lereya', 'Daniel Lereya', 'Platform into product', ['Product visionary', 'Deep technical'],
    [80, 84, 58, 82, 40, 12], { CPO: A.perfect, CTO: A.great, COO: A.good, CEO: A.poor }),

  // ── Lemonade ────────────────────────────────────────────────────────────
  p('daniel-schreiber', 'Daniel Schreiber', 'Insurance you almost enjoy', ['Storyteller', 'Fundraising monster'],
    [80, 54, 84, 76, 90, 32], { CEO: A.perfect, GROWTH: A.great, CPO: A.good, CTO: A.awful }),
  p('shai-wininger', 'Shai Wininger', 'Builds the whole product twice', ['Deep technical', 'Product visionary'],
    [90, 92, 74, 80, 70, 26], { CTO: A.perfect, CPO: A.great, CEO: A.great, GROWTH: A.good }),
  p('ty-sagalow', 'Ty Sagalow', 'Wrote the policy from scratch', ['Elite operator', 'Underrated'],
    [56, 40, 48, 88, 50, 10], { COO: A.perfect, WILDCARD: A.good, CEO: A.poor, GROWTH: A.poor, CTO: A.awful }),

  // ── Mobileye ────────────────────────────────────────────────────────────
  p('amnon-shashua', 'Amnon Shashua', 'One camera is enough', ['Deep technical', 'Legend'],
    [78, 98, 60, 80, 84, 24], { CTO: A.perfect, CEO: A.great, CPO: A.good, GROWTH: A.poor }),
  p('ziv-aviram', 'Ziv Aviram', 'Drove it all the way to the IPO', ['Elite operator', 'Ruthless'],
    [48, 58, 72, 94, 80, 26], { COO: A.perfect, CEO: A.great, GROWTH: A.good, CTO: A.awful }),
  p('gaby-hayon', 'Gaby Hayon', 'Twenty years of R&D', ['Deep technical', 'Heads down'],
    [54, 92, 34, 78, 32, 10], { CTO: A.perfect, COO: A.ok, CEO: A.awful, GROWTH: A.awful }),

  // ── Fiverr ──────────────────────────────────────────────────────────────
  p('micha-kaufman', 'Micha Kaufman', 'Everything for five dollars', ['Growth machine', 'Product visionary'],
    [78, 66, 90, 80, 78, 34], { CEO: A.perfect, GROWTH: A.great, CPO: A.good, CTO: A.poor }),
  p('gali-arnon', 'Gali Arnon', 'In doers we trust', ['Brand', 'Distribution'],
    [54, 30, 88, 70, 44, 26], { GROWTH: A.perfect, CPO: A.ok, CEO: A.poor, CTO: A.awful }),

  // ── Check Point ─────────────────────────────────────────────────────────
  p('gil-shwed', 'Gil Shwed', 'Invented the firewall at 24', ['Deep technical', 'Legend'],
    [76, 96, 66, 92, 78, 14], { CTO: A.perfect, CEO: A.great, COO: A.great, CPO: A.good }),
  p('marius-nacht', 'Marius Nacht', 'Quiet half of the empire', ['Elite operator', 'Low drama'],
    [52, 74, 60, 86, 82, 12], { COO: A.perfect, CEO: A.good, WILDCARD: 0.98, CTO: A.ok, GROWTH: A.poor }),
  p('shlomo-kramer', 'Shlomo Kramer', 'Founded three of them', ['Deep technical', 'Fundraising monster'],
    [80, 92, 64, 74, 80, 28], { CTO: A.perfect, CEO: A.great, CPO: A.good, COO: A.ok }),
  p('dorit-dor', 'Dorit Dor', 'Owns the product and the threat', ['Product visionary', 'Deep technical'],
    [82, 88, 48, 84, 38, 10], { CPO: A.perfect, CTO: A.great, COO: A.good, GROWTH: A.poor }),

  // ── SaaS / product founders ─────────────────────────────────────────────
  p('patrick-collison', 'Patrick Collison', 'Seven lines of code', ['Deep technical', 'Product visionary'],
    [92, 94, 80, 86, 92, 20], { CEO: A.perfect, CTO: A.great, CPO: A.great, GROWTH: A.good }),
  p('drew-houston', 'Drew Houston', 'A folder that just syncs', ['Deep technical', 'Product visionary'],
    [86, 88, 76, 74, 72, 22], { CPO: A.perfect, CEO: A.great, CTO: A.great, COO: A.ok }),
  p('dylan-field', 'Dylan Field', 'Multiplayer for designers', ['Product visionary', 'Design obsessive'],
    [96, 80, 78, 76, 86, 26], { CPO: A.perfect, CEO: A.great, CTO: A.good, GROWTH: A.good }),
  p('ivan-zhao', 'Ivan Zhao', 'Lego bricks for software', ['Product visionary', 'Design obsessive'],
    [94, 76, 70, 62, 70, 30], { CPO: A.perfect, CEO: A.good, CTO: A.ok, COO: A.poor }),
  p('vlad-magdalin', 'Vlad Magdalin', 'Ten years to no-code', ['Deep technical', 'Relentless'],
    [86, 86, 66, 68, 58, 24], { CTO: A.perfect, CPO: A.great, CEO: A.ok, GROWTH: A.poor }),

  // ── AI researchers ──────────────────────────────────────────────────────
  p('geoffrey-hinton', 'Geoffrey Hinton', 'Godfather with regrets', ['Deep technical', 'Legend'],
    [58, 100, 50, 54, 52, 40], { CTO: A.perfect, WILDCARD: 1.02, CEO: A.awful, COO: A.awful, GROWTH: A.poor }),
  p('yann-lecun', 'Yann LeCun', 'Convolutions and quote tweets', ['Deep technical', 'Opinionated'],
    [62, 98, 60, 62, 54, 46], { CTO: A.perfect, WILDCARD: 1.02, CEO: A.poor, COO: A.awful }),
  p('yoshua-bengio', 'Yoshua Bengio', 'The one who worries', ['Deep technical', 'Low drama'],
    [56, 97, 40, 60, 58, 20], { CTO: A.perfect, WILDCARD: 0.98, CEO: A.awful, GROWTH: A.awful }),

  // ── Consumer social ─────────────────────────────────────────────────────
  p('kevin-systrom', 'Kevin Systrom', 'Filters, then an exit', ['Product visionary', 'Design obsessive'],
    [92, 70, 84, 74, 72, 24], { CPO: A.perfect, CEO: A.great, GROWTH: A.great, CTO: A.ok }),
  p('evan-spiegel', 'Evan Spiegel', 'Said no to three billion', ['Product visionary', 'High chaos'],
    [90, 64, 86, 66, 84, 64], { CPO: A.perfect, CEO: A.great, GROWTH: A.good, COO: A.poor }),
  p('ben-silbermann', 'Ben Silbermann', 'Boards, quietly compounding', ['Product visionary', 'Low drama'],
    [86, 62, 76, 74, 70, 16], { CPO: A.perfect, CEO: A.good, GROWTH: A.good, CTO: A.poor }),

  // ── Developer tools ─────────────────────────────────────────────────────
  p('guillermo-rauch', 'Guillermo Rauch', 'Ships on every push', ['Deep technical', 'Distribution'],
    [88, 92, 82, 76, 74, 22], { CTO: A.perfect, CPO: A.great, CEO: A.great, GROWTH: A.good }),
  p('mitchell-hashimoto', 'Mitchell Hashimoto', 'Infrastructure as a hobby', ['Deep technical', 'Legend'],
    [78, 97, 58, 72, 58, 14], { CTO: A.perfect, CPO: A.good, CEO: A.poor, GROWTH: A.poor }),
  p('nat-friedman', 'Nat Friedman', "Bought the world's code", ['Elite operator', 'Product visionary'],
    [84, 84, 74, 88, 78, 20], { CEO: A.great, CPO: A.great, COO: A.great, CTO: A.good }),
  p('sid-sijbrandij', 'Sid Sijbrandij', 'The whole handbook is public', ['Elite operator', 'Deep technical'],
    [72, 82, 70, 86, 72, 18], { COO: A.perfect, CEO: A.great, CTO: A.good, CPO: A.ok }),

  // ── Growth, enterprise, commerce, marketplaces ──────────────────────────
  p('andrew-chen', 'Andrew Chen', 'Wrote the cold start playbook', ['Growth machine', 'Brand'],
    [64, 52, 92, 66, 70, 20], { GROWTH: A.perfect, WILDCARD: 1.02, CPO: A.ok, COO: A.poor, CTO: A.awful }),
  p('larry-ellison', 'Larry Ellison', 'Wins the deal and the regatta', ['Ruthless', 'Fundraising monster'],
    [58, 72, 94, 88, 96, 82], { CEO: A.perfect, GROWTH: A.great, COO: A.good, CPO: A.poor }),
  p('parker-conrad', 'Parker Conrad', 'Fired, then did it again', ['High chaos', 'Product visionary'],
    [88, 78, 74, 72, 80, 68], { CPO: A.perfect, CEO: A.great, CTO: A.good, COO: A.poor }),
  p('jack-ma', 'Jack Ma', 'Teacher turned emperor', ['Storyteller', 'Growth machine'],
    [68, 44, 96, 86, 94, 74], { CEO: A.perfect, GROWTH: A.perfect, COO: A.good, CTO: A.awful }),
  p('pierre-omidyar', 'Pierre Omidyar', 'A marketplace for junk', ['Product visionary', 'Low drama'],
    [82, 78, 74, 66, 68, 18], { CPO: A.great, CTO: A.good, CEO: A.ok, COO: A.poor }),
  p('logan-green', 'Logan Green', 'The friendly pink moustache', ['Product visionary', 'Low drama'],
    [76, 62, 74, 72, 74, 24], { CPO: A.great, CEO: A.good, GROWTH: A.good, CTO: A.poor }),
  p('tony-xu', 'Tony Xu', 'Delivered the suburbs', ['Elite operator', 'Relentless'],
    [72, 70, 90, 94, 84, 30], { CEO: A.perfect, COO: A.great, GROWTH: A.great, CTO: A.poor }),
  p('palmer-luckey', 'Palmer Luckey', 'Duct-taped the future to his face', ['Deep technical', 'High chaos'],
    [84, 88, 70, 58, 80, 86], { CTO: A.great, CPO: A.great, WILDCARD: 1.06, CEO: A.poor, COO: A.awful }),
  p('daniel-ek', 'Daniel Ek', 'Licensed the whole catalogue', ['Elite operator', 'Growth machine'],
    [84, 80, 88, 88, 90, 26], { CEO: A.perfect, GROWTH: A.great, CPO: A.good, CTO: A.ok }),
  p('meg-whitman', 'Meg Whitman', 'Ran everything, then Quibi', ['Elite operator', 'Enterprise sales'],
    [48, 50, 74, 92, 80, 34], { COO: A.perfect, CEO: A.great, GROWTH: A.good, CPO: A.poor, CTO: A.awful }),

  // ── Israeli cyber mafia ─────────────────────────────────────────────────
  p('tomer-weingarten', 'Tomer Weingarten', 'Endpoint, autonomously', ['Deep technical', 'Elite operator'],
    [76, 88, 76, 84, 82, 26], { CTO: A.perfect, CEO: A.great, COO: A.good, CPO: A.good }),
  p('udi-mokady', 'Udi Mokady', 'Owns the privileged account', ['Elite operator', 'Enterprise sales'],
    [62, 72, 80, 92, 84, 16], { COO: A.perfect, CEO: A.great, GROWTH: A.great, CTO: A.ok }),
  p('mickey-boodaei', 'Mickey Boodaei', 'Serial exit machine', ['Deep technical', 'Fundraising monster'],
    [74, 88, 72, 80, 86, 24], { CTO: A.perfect, CEO: A.great, GROWTH: A.good, COO: A.ok }),

  // ── Crypto ──────────────────────────────────────────────────────────────
  p('anatoly-yakovenko', 'Anatoly Yakovenko', 'Proof of history, mostly', ['Deep technical', 'High chaos'],
    [70, 94, 72, 58, 74, 66], { CTO: A.perfect, WILDCARD: 1.02, CEO: A.poor, COO: A.awful }),
];

export const PEOPLE: Record<PersonId, Person> = Object.fromEntries(
  ROSTER.map((person) => [person.id, person]),
);

export const getPerson = (id: PersonId): Person => {
  const person = PEOPLE[id];
  if (!person) throw new Error(`Unknown person: ${id}`);
  return person;
};

export const ALL_PEOPLE = ROSTER;
